import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import {
  required,
  composeValidators,
  optional,
  ytunnusValid,
  hetuValid,
  finnishPostalCode,
  ibanValidation,
  normalizeIban,
} from '../../util/validators';
import { ensureCurrentUser } from '../../util/data';
import { prhLookup } from '../../util/api';
import getCountryCodes from '../../translations/countryCodes';
import { EU_VAT_AREA } from '../../util/vat';
import { ytunnusToVat } from '../../containers/TaxInformationPage/TaxInformationPage.duck';
import {
  Form,
  PrimaryButton,
  InlineTextButton,
  FieldTextInput,
  FieldBirthdayInput,
  FieldCheckbox,
  FieldRadioButton,
  FieldSelect,
  ExternalLink,
  IconSpinner,
} from '../../components';

import css from './TaxInformationForm.css';

// EU 27 member states. Finland first (most users), then alphabetical by ISO code
// to keep the order language-neutral. DAC7 reporting only applies to sellers
// resident in an EU member state — non-EU users get a separate option below.
const EU_COUNTRIES = [
  { code: 'FI', labelKey: 'Verotiedot.country.FI' },
  { code: 'AT', labelKey: 'Verotiedot.country.AT' },
  { code: 'BE', labelKey: 'Verotiedot.country.BE' },
  { code: 'BG', labelKey: 'Verotiedot.country.BG' },
  { code: 'CY', labelKey: 'Verotiedot.country.CY' },
  { code: 'CZ', labelKey: 'Verotiedot.country.CZ' },
  { code: 'DE', labelKey: 'Verotiedot.country.DE' },
  { code: 'DK', labelKey: 'Verotiedot.country.DK' },
  { code: 'EE', labelKey: 'Verotiedot.country.EE' },
  { code: 'ES', labelKey: 'Verotiedot.country.ES' },
  { code: 'FR', labelKey: 'Verotiedot.country.FR' },
  { code: 'GR', labelKey: 'Verotiedot.country.GR' },
  { code: 'HR', labelKey: 'Verotiedot.country.HR' },
  { code: 'HU', labelKey: 'Verotiedot.country.HU' },
  { code: 'IE', labelKey: 'Verotiedot.country.IE' },
  { code: 'IT', labelKey: 'Verotiedot.country.IT' },
  { code: 'LT', labelKey: 'Verotiedot.country.LT' },
  { code: 'LU', labelKey: 'Verotiedot.country.LU' },
  { code: 'LV', labelKey: 'Verotiedot.country.LV' },
  { code: 'MT', labelKey: 'Verotiedot.country.MT' },
  { code: 'NL', labelKey: 'Verotiedot.country.NL' },
  { code: 'PL', labelKey: 'Verotiedot.country.PL' },
  { code: 'PT', labelKey: 'Verotiedot.country.PT' },
  { code: 'RO', labelKey: 'Verotiedot.country.RO' },
  { code: 'SE', labelKey: 'Verotiedot.country.SE' },
  { code: 'SI', labelKey: 'Verotiedot.country.SI' },
  { code: 'SK', labelKey: 'Verotiedot.country.SK' },
];

const isEuCountry = code => EU_VAT_AREA.includes(code);

// Statuses a seller can pick: three in Finland (invoicing services are a
// Finnish phenomenon), two elsewhere in the EU, none outside the EU where
// every seller is REGISTERED with a self-declared home rate (Notion Q4, Q11).
export const vatStatusOptions = (country, accountRole) => {
  if (!country || !isEuCountry(country)) return [];
  return country === 'FI'
    ? ['REGISTERED', 'EXEMPT_SMALL_BUSINESS', 'INVOICING_SERVICE']
    : ['REGISTERED', 'EXEMPT_SMALL_BUSINESS'];
};

const Y_TUNNUS_DEBOUNCE_MS = 350;

// Convert legacy YYYY-MM-DD or { year, month, day } into the birthday input
// shape expected by FieldBirthdayInput.
const initialBirthDate = dateLike => {
  if (!dateLike) return null;
  if (typeof dateLike === 'object' && dateLike.year) return dateLike;
  if (typeof dateLike === 'string') {
    const m = dateLike.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { year: m[1], month: m[2], day: m[3] };
  }
  return null;
};

const maskedDisplay = last4 => `••••••-•••${last4 || '?'}`;

// Fields that must not survive a tax-residency country change. The FI
// classification (or non-FI sellerType) is kept when it still applies;
// identifiers, VAT and IBAN are country-specific and always reset.
export const countryResetPatch = (newCountry, prevValues) => ({
  ytunnus: '',
  hetu: '',
  tin: '',
  tinCountry: '',
  noTin: false,
  finCompanyForm: newCountry === 'FI' ? prevValues?.finCompanyForm : undefined,
  finHasYtunnus: false,
  sellerType: newCountry === 'FI' ? undefined : prevValues?.sellerType,
  businessRegNumber: '',
  businessRegCountry: '',
  iban: '',
  vatStatus: undefined,
  vatNumber: '',
  exemptId: '',
  homeVatRate: '0',
  homeVatLabel: 'VAT',
  invoicingServiceName: '',
  invoicingServiceVatNumber: '',
});

class TaxInformationFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // PRH lookup state, keyed by Y-tunnus string
      prhInFlight: false,
      prhResult: null,
      prhError: null,
      lastLookupId: null,
      // Whether the form is showing a masked HETU/TIN instead of an input.
      hetuMasked: !!props.initialMaskedIdentifier,
      lastLookedUpAt: null,
    };
    this.prhTimer = null;
  }

  componentWillUnmount() {
    if (this.prhTimer) window.clearTimeout(this.prhTimer);
  }

  componentDidUpdate(prevProps) {
    // After a successful save the identifier is encrypted at rest and its
    // plaintext is gone from the form — show the masked display again so the
    // next save keeps the stored value instead of requiring a re-type.
    if (
      !prevProps.taxInformationSuccess &&
      this.props.taxInformationSuccess &&
      this.props.initialMaskedIdentifier &&
      !this.state.hetuMasked
    ) {
      this.setState({ hetuMasked: true });
    }
  }

  scheduleLookup = (businessId, applyPrh) => {
    if (this.prhTimer) window.clearTimeout(this.prhTimer);
    this.prhTimer = window.setTimeout(
      () => this.runLookup(businessId, applyPrh),
      Y_TUNNUS_DEBOUNCE_MS
    );
  };

  runLookup = (businessId, applyPrh) => {
    if (!businessId || businessId === this.state.lastLookupId) return;
    this.setState({ prhInFlight: true, prhError: null, lastLookupId: businessId });
    prhLookup(businessId)
      .then(result => {
        this.setState({
          prhInFlight: false,
          prhResult: result,
          lastLookedUpAt: Date.now(),
        });
        if (applyPrh) applyPrh(result);
      })
      .catch(e => {
        this.setState({ prhInFlight: false, prhError: e.message || 'lookup failed' });
      });
  };

  revealHetuInput = (formApi, identifierFieldName) => () => {
    formApi.change(identifierFieldName, '');
    this.setState({ hetuMasked: false });
  };

  render() {
    return (
      <FinalForm
        {...this.props}
        // initialValues are recomputed on every page render and contain a
        // nested dateOfBirth object — deep-compare so the form doesn't
        // reinitialize (wiping user input) on unrelated re-renders.
        initialValuesEqual={isEqual}
        // The masked-identifier state lives outside the form values; attach
        // it at submit time so the payload can keep the stored identifier.
        onSubmit={values => this.props.onSubmit({ ...values, hetuMasked: this.state.hetuMasked })}
        mutators={{
          setFields: ([fields], state, { changeValue }) => {
            Object.keys(fields).forEach(name => {
              changeValue(state, name, () => fields[name]);
            });
          },
        }}
        render={fieldRenderProps => {
          const {
            rootClassName,
            className,
            currentUser,
            formId,
            handleSubmit,
            inProgress,
            intl,
            invalid,
            values,
            taxInformationSuccess,
            taxInformationError,
            vatValidationStatus,
            pristine,
            form,
            initialMaskedIdentifier,
            vatStatusNeedsConfirmation,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);
          if (!user.id) return null;

          const accountRole = user.attributes.profile.publicData?.account_role;
          const country = values?.country || 'FI';
          const noTin = !!values?.noTin;
          const isFi = country === 'FI';
          const isNonEu = !!country && !isEuCountry(country);
          const showIban = isFi && accountRole === 'freelancer';
          const vatStatus = values?.vatStatus;
          const statusOptions = vatStatusOptions(country, accountRole);
          const vatUnverified = !!(taxInformationError?.errors || []).includes(
            'vat.number.unverified'
          );

          // Every user picks an explicit company-form choice up front. The
          // labels differ between FI and the rest of the EU but the resulting
          // entity/individual classification is the same.
          //   FI:   finCompanyForm = ENTITY | INDIVIDUAL_NO_BUSINESS_ID
          //   EU:   sellerType     = BUSINESS | INDIVIDUAL
          const finCompanyForm = values?.finCompanyForm;
          const finHasYtunnus = !!values?.finHasYtunnus;
          const effectiveEntityType = isFi
            ? finCompanyForm === 'ENTITY'
              ? 'ENTITY'
              : finCompanyForm === 'INDIVIDUAL_NO_BUSINESS_ID'
              ? 'INDIVIDUAL'
              : null
            : values?.sellerType === 'BUSINESS'
            ? 'ENTITY'
            : values?.sellerType === 'INDIVIDUAL'
            ? 'INDIVIDUAL'
            : null;

          const isEntity = effectiveEntityType === 'ENTITY';
          const isIndividual = effectiveEntityType === 'INDIVIDUAL';
          // Hide everything below the classification radio until the user picks.
          const awaitingClassification = !effectiveEntityType;

          const informationRequired = intl.formatMessage({
            id: 'SignupProviderForms.informationRequired',
          });

          const dac7InfoUrl = intl.locale?.toLowerCase().startsWith('en')
            ? 'https://www.vero.fi/en/detailed-guidance/guidance/209369/the-information-reporting-requirement-of-a-reporting-platform-operator--dac7/'
            : 'https://www.vero.fi/syventavat-vero-ohjeet/ohje-hakusivu/209369/raportoivan-alustaoperaattorin-tiedonantovelvollisuus-dac7/';

          const dac7DueDiligenceUrl = intl.locale?.toLowerCase().startsWith('en')
            ? 'https://www.vero.fi/en/businesses-and-corporations/taxes-and-charges/annual-information-returns/dac7/due-diligence-obligations-for-platform-operators/'
            : 'https://www.vero.fi/yritykset-ja-yhteisot/verot-ja-maksut/vuosiilmoitus/dac7/huolellisuusvelvoitteet-alustaoperaattorille/';

          const taxInformationInfo = (
            <FormattedMessage
              id="SignupForm.taxInformationInfo"
              values={{
                a: chunks => (
                  <ExternalLink key="dac7-info" href={dac7InfoUrl}>
                    {chunks}
                  </ExternalLink>
                ),
              }}
            />
          );

          const classes = classNames(rootClassName || css.root, className);
          // A migrated or last-year exempt VAT status needs the user's
          // confirmation (vatStatusReminder), and saving the unchanged form
          // is how they give it — the server records the declaration.
          const confirmingVatStatus = vatStatusNeedsConfirmation && pristine;
          const submitDisabled =
            invalid ||
            inProgress ||
            (pristine && !vatStatusNeedsConfirmation) ||
            (!isNonEu && statusOptions.length > 0 && !vatStatus);

          const countrySelect = (
            <FieldSelect
              id={formId ? `${formId}.country` : 'country'}
              name="country"
              label={intl.formatMessage({ id: 'Verotiedot.taxResidency.label' })}
              className={css.field}
              validate={required(informationRequired)}
            >
              {EU_COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {intl.formatMessage({ id: c.labelKey, defaultMessage: c.code })}
                </option>
              ))}
              <optgroup label={intl.formatMessage({ id: 'Verotiedot.country.otherGroup' })}>
                {getCountryCodes(intl.locale)
                  // Åland sellers choose Finland and a 22xxx postcode.
                  .filter(c => !isEuCountry(c.code) && c.code !== 'AX')
                  .map(c => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
              </optgroup>
            </FieldSelect>
          );

          // FI classification radio. Mirrors the non-FI sellerType radio but
          // uses the local terminology (toiminimi / kevytyrittäjä vs muu).
          const finCompanyFormRadio = isFi ? (
            <div className={css.field}>
              <label className={css.sellerTypeLabel}>
                <FormattedMessage id="Verotiedot.finCompanyForm.label" />
              </label>
              <div
                className={classNames(css.sellerTypeOptions, {
                  [css.sellerTypeOptionsHasSelection]: !!finCompanyForm,
                })}
              >
                <FieldRadioButton
                  id={formId ? `${formId}.finCompanyForm.individual` : 'finCompanyForm.individual'}
                  name="finCompanyForm"
                  value="INDIVIDUAL_NO_BUSINESS_ID"
                  label={intl.formatMessage({ id: 'Verotiedot.finCompanyForm.individual' })}
                  validate={required(informationRequired)}
                />
                <FieldRadioButton
                  id={formId ? `${formId}.finCompanyForm.entity` : 'finCompanyForm.entity'}
                  name="finCompanyForm"
                  value="ENTITY"
                  label={intl.formatMessage({ id: 'Verotiedot.finCompanyForm.entity' })}
                  validate={required(informationRequired)}
                />
              </div>
            </div>
          ) : null;

          // Non-FI: explicit "Who are you?" radio. TIN is the universal field,
          // but the rest of the form (legal name vs personal name, optional
          // business registration number, optional VAT) depends on this.
          const sellerTypeRadio = !isFi ? (
            <div className={css.field}>
              <label className={css.sellerTypeLabel}>
                <FormattedMessage id="Verotiedot.sellerType.label" />
              </label>
              <div
                className={classNames(css.sellerTypeOptions, {
                  [css.sellerTypeOptionsHasSelection]: !!values?.sellerType,
                })}
              >
                <FieldRadioButton
                  id={formId ? `${formId}.sellerType.individual` : 'sellerType.individual'}
                  name="sellerType"
                  value="INDIVIDUAL"
                  label={intl.formatMessage({ id: 'Verotiedot.sellerType.individual' })}
                  validate={required(informationRequired)}
                />
                <FieldRadioButton
                  id={formId ? `${formId}.sellerType.business` : 'sellerType.business'}
                  name="sellerType"
                  value="BUSINESS"
                  label={intl.formatMessage({ id: 'Verotiedot.sellerType.business' })}
                  validate={required(informationRequired)}
                />
              </div>
              <p className={css.helpText}>
                <FormattedMessage id="Verotiedot.sellerType.help" />
              </p>
            </div>
          ) : null;

          // After the classification radio: simple flags. No toiminimi-toggle
          // hybrid state anymore — the radio fully determines the path.
          const showsAsIndividual = isIndividual;
          const showsAsEntity = isEntity;

          const applyPrh = result => {
            if (!result) return;
            if (result.type === 'IndividualSeller') {
              // PRH miss / toiminimi — flip to the individual flow and keep
              // the Y-tunnus the user already typed.
              form.mutators.setFields({
                finCompanyForm: 'INDIVIDUAL_NO_BUSINESS_ID',
                finHasYtunnus: true,
              });
              return;
            }
            // EntitySeller — pre-fill where the user hasn't already typed.
            const patch = {};
            if (!values?.legalName && result.name) patch.legalName = result.name;
            if (!values?.companyForm && result.companyForm) patch.companyForm = result.companyForm;
            if (result.address) {
              if (!values?.street && result.address.street) patch.street = result.address.street;
              if (!values?.postcode && result.address.postCode)
                patch.postcode = result.address.postCode;
              if (!values?.city && result.address.city) patch.city = result.address.city;
            }
            if (Object.keys(patch).length > 0) {
              form.mutators.setFields(patch);
            }
          };

          // Reusable Y-tunnus input — used both for FI entities (primary
          // identifier, PRH-looked-up) and for FI individuals who optionally
          // declare a business ID. The `onLookup` flag controls both the
          // network call and the "we look this up automatically" help text;
          // for individuals we skip the lookup, so showing that text would
          // be misleading.
          const renderYtunnusInput = ({ onLookup, fieldId }) => (
            <>
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.${fieldId}` : fieldId}
                name="ytunnus"
                label={intl.formatMessage({ id: 'Verotiedot.ytunnus.label' })}
                placeholder="1234567-8"
                onBlur={e => {
                  const val = (e?.target?.value || '').trim();
                  if (onLookup && val && /^\d{7}-\d$/.test(val)) {
                    this.scheduleLookup(val, applyPrh);
                  }
                }}
                validate={composeValidators(
                  required(informationRequired),
                  ytunnusValid(intl.formatMessage({ id: 'Verotiedot.ytunnus.invalid' }))
                )}
              />
              {onLookup ? (
                <div className={css.helpText}>
                  {this.state.prhInFlight ? (
                    <span className={css.prhStatus}>
                      <IconSpinner /> {intl.formatMessage({ id: 'Verotiedot.ytunnus.looking' })}
                    </span>
                  ) : (
                    intl.formatMessage({ id: 'Verotiedot.ytunnus.help' })
                  )}
                </div>
              ) : null}
              {onLookup && this.state.prhError ? (
                <p className={css.errorMessage}>{this.state.prhError}</p>
              ) : null}
            </>
          );

          // Primary identifier field group
          const identifierGroup = (() => {
            if (isFi && showsAsEntity) {
              return (
                <div className={css.field}>
                  {renderYtunnusInput({ onLookup: true, fieldId: 'ytunnus' })}
                </div>
              );
            }

            if (isFi && showsAsIndividual) {
              return (
                <div className={css.field}>
                  {this.state.hetuMasked ? (
                    <div className={css.maskedField}>
                      <label>{intl.formatMessage({ id: 'Verotiedot.hetu.label' })}</label>
                      <div className={css.maskedValue}>
                        {maskedDisplay(initialMaskedIdentifier?.last4)}
                      </div>
                      <InlineTextButton
                        type="button"
                        onClick={this.revealHetuInput(form, 'hetu')}
                        className={css.changeButton}
                      >
                        <FormattedMessage id="Verotiedot.hetu.change" />
                      </InlineTextButton>
                    </div>
                  ) : (
                    <>
                      <FieldTextInput
                        type="password"
                        id={formId ? `${formId}.hetu` : 'hetu'}
                        name="hetu"
                        label={intl.formatMessage({ id: 'Verotiedot.hetu.label' })}
                        placeholder="ddmmyy-XXXC"
                        autoComplete="off"
                        validate={composeValidators(
                          required(informationRequired),
                          hetuValid(intl.formatMessage({ id: 'Verotiedot.hetu.invalid' }))
                        )}
                      />
                      <p className={css.privacyNote}>
                        <FormattedMessage
                          id="Verotiedot.hetu.privacy"
                          values={{
                            a: chunks => (
                              <ExternalLink key="dac7-due-diligence" href={dac7DueDiligenceUrl}>
                                {chunks}
                              </ExternalLink>
                            ),
                          }}
                        />
                      </p>
                    </>
                  )}
                  <div className={css.finHasYtunnusToggle}>
                    <FieldCheckbox
                      id={formId ? `${formId}.finHasYtunnus` : 'finHasYtunnus'}
                      name="finHasYtunnus"
                      label={intl.formatMessage({ id: 'Verotiedot.finHasYtunnus.label' })}
                    />
                  </div>
                  {finHasYtunnus ? (
                    <div className={css.nestedField}>
                      {renderYtunnusInput({ onLookup: false, fieldId: 'finIndividualYtunnus' })}
                    </div>
                  ) : null}
                </div>
              );
            }

            // Non-FI: TIN is the universal identifier for everyone, with an
            // issuing-country dropdown and an opt-out "no TIN" toggle that
            // reveals the birth-info fallback block.
            const tinHelpText = isEntity
              ? 'Verotiedot.tin.helpBusiness'
              : 'Verotiedot.tin.helpIndividual';
            return (
              <div className={css.field}>
                {!noTin ? (
                  <>
                    <FieldTextInput
                      type="text"
                      id={formId ? `${formId}.tin` : 'tin'}
                      name="tin"
                      label={intl.formatMessage({ id: 'Verotiedot.tin.label' })}
                      placeholder={intl.formatMessage({ id: 'Verotiedot.tin.label' })}
                      validate={required(informationRequired)}
                    />
                    <p className={css.helpText}>
                      <FormattedMessage id={tinHelpText} />
                    </p>
                    <FieldSelect
                      id={formId ? `${formId}.tinCountry` : 'tinCountry'}
                      name="tinCountry"
                      label={intl.formatMessage({ id: 'Verotiedot.tin.country' })}
                      validate={required(informationRequired)}
                    >
                      {EU_COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                          {intl.formatMessage({ id: c.labelKey, defaultMessage: c.code })}
                        </option>
                      ))}
                    </FieldSelect>
                    <p className={css.helpText}>
                      <FormattedMessage
                        id="Verotiedot.tin.help"
                        values={{
                          a: chunks => (
                            <ExternalLink
                              key="tin-checker"
                              href="https://ec.europa.eu/taxation_customs/tin/#/check-tin"
                            >
                              {chunks}
                            </ExternalLink>
                          ),
                        }}
                      />
                    </p>
                  </>
                ) : null}
                <FieldCheckbox
                  id={formId ? `${formId}.noTin` : 'noTin'}
                  name="noTin"
                  label={intl.formatMessage({ id: 'Verotiedot.tin.noTin' })}
                />
              </div>
            );
          })();

          // Optional business registration number — only relevant for non-FI
          // businesses. FI businesses already provide their Y-tunnus as the
          // primary identifier and don't need a separate registration number.
          const businessRegBlock =
            !isFi && isEntity && !noTin ? (
              <div className={css.field}>
                <FieldTextInput
                  type="text"
                  id={formId ? `${formId}.businessRegNumber` : 'businessRegNumber'}
                  name="businessRegNumber"
                  label={intl.formatMessage({ id: 'Verotiedot.businessReg.label' })}
                  placeholder={intl.formatMessage({ id: 'Verotiedot.businessReg.placeholder' })}
                />
                <FieldSelect
                  id={formId ? `${formId}.businessRegCountry` : 'businessRegCountry'}
                  name="businessRegCountry"
                  label={intl.formatMessage({ id: 'Verotiedot.businessReg.country' })}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'Verotiedot.businessReg.countryPlaceholder' })}
                  </option>
                  {EU_COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>
                      {intl.formatMessage({ id: c.labelKey, defaultMessage: c.code })}
                    </option>
                  ))}
                </FieldSelect>
                <p className={css.helpText}>
                  <FormattedMessage id="Verotiedot.businessReg.help" />
                </p>
              </div>
            ) : null;

          // Name fields
          const nameGroup = showsAsEntity ? (
            <div className={css.field}>
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.legalName` : 'legalName'}
                name="legalName"
                label={intl.formatMessage({ id: 'SignupProviderForms.companyLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupProviderForms.companyLabel' })}
                validate={required(informationRequired)}
              />
            </div>
          ) : (
            <div className={css.nameRow}>
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.firstName` : 'firstName'}
                name="firstName"
                label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                validate={required(informationRequired)}
              />
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.lastName` : 'lastName'}
                name="lastName"
                label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                validate={required(informationRequired)}
              />
            </div>
          );

          // Address fields
          const postcodeValidator = isFi
            ? composeValidators(
                required(informationRequired),
                finnishPostalCode(intl.formatMessage({ id: 'Verotiedot.postcode.invalidFi' }))
              )
            : required(informationRequired);

          const addressGroup = (
            <>
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.street` : 'street'}
                name="street"
                label={intl.formatMessage({ id: 'SignupProviderForms.addressLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupProviderForms.addressLabel' })}
                validate={required(informationRequired)}
              />
              <div className={css.addressRow}>
                <FieldTextInput
                  type="text"
                  id={formId ? `${formId}.postcode` : 'postcode'}
                  name="postcode"
                  label={intl.formatMessage({ id: 'SignupProviderForms.postalCodeLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupProviderForms.postalCodeLabel' })}
                  format={v => (typeof v === 'string' ? v.trim() : v)}
                  formatOnBlur
                  validate={postcodeValidator}
                />
                <FieldTextInput
                  type="text"
                  id={formId ? `${formId}.city` : 'city'}
                  name="city"
                  label={intl.formatMessage({ id: 'SignupProviderForms.cityLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupProviderForms.cityLabel' })}
                  validate={required(informationRequired)}
                />
              </div>
            </>
          );

          // VAT status block (spec §5). Inside the EU: a status radio and the
          // status-specific fields. Outside: no radio, a home rate and label.
          // Required for REGISTERED; optional for EXEMPT_SMALL_BUSINESS (a
          // number for EU sales, Matrix A CS-5) and outside the EU.
          const vatNumberFieldFor = ({ isOptional, labelId }) => (
            <div className={css.field}>
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.vatNumber` : 'vatNumber'}
                name="vatNumber"
                label={intl.formatMessage({ id: labelId })}
                placeholder={intl.formatMessage({ id: 'Verotiedot.vatNumber.placeholder' })}
                format={v => (typeof v === 'string' ? v.replace(/[\s.\-]/g, '').toUpperCase() : v)}
                formatOnBlur
                validate={isOptional ? optional() : required(informationRequired)}
              />
              <p className={css.helpText}>
                <FormattedMessage id="Verotiedot.vatNumber.help" />
              </p>
              {vatUnverified ? (
                <p className={css.errorMessage}>
                  <FormattedMessage id="TaxInformationForm.vatNumberUnverified" />
                </p>
              ) : null}
              {vatValidationStatus === 'PENDING' ? (
                <p className={css.helpText}>
                  <FormattedMessage id="TaxInformationForm.vatPending" />
                </p>
              ) : null}
            </div>
          );

          const vatNumberField = vatNumberFieldFor({
            isOptional: isNonEu,
            labelId: 'Verotiedot.vatNumber.label',
          });
          const optionalVatNumberField = vatNumberFieldFor({
            isOptional: true,
            labelId: 'Verotiedot.vatNumber.optionalLabel',
          });

          const vatStatusBlock = isNonEu ? (
            <div className={css.field}>
              {vatNumberField}
              <FieldTextInput
                type="number"
                id={formId ? `${formId}.homeVatRate` : 'homeVatRate'}
                name="homeVatRate"
                label={intl.formatMessage({ id: 'Verotiedot.homeVat.rate' })}
                placeholder="0"
                min="0"
                max="100"
                step="0.1"
                validate={required(informationRequired)}
              />
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.homeVatLabel` : 'homeVatLabel'}
                name="homeVatLabel"
                label={intl.formatMessage({ id: 'Verotiedot.homeVat.label' })}
                placeholder="VAT"
                maxLength={20}
                validate={required(informationRequired)}
              />
              <p className={css.helpText}>
                <FormattedMessage id="Verotiedot.homeVat.responsibility" />
              </p>
            </div>
          ) : (
            <div className={css.field}>
              <label className={css.sellerTypeLabel}>
                <FormattedMessage id="Verotiedot.vatStatus.label" />
              </label>
              <div
                className={classNames(css.sellerTypeOptions, {
                  [css.sellerTypeOptionsHasSelection]: !!vatStatus,
                })}
              >
                {statusOptions.map(option => (
                  <FieldRadioButton
                    key={option}
                    id={formId ? `${formId}.vatStatus.${option}` : `vatStatus.${option}`}
                    name="vatStatus"
                    value={option}
                    label={intl.formatMessage({ id: `Verotiedot.vatStatus.${option}` })}
                  />
                ))}
              </div>
              <p className={css.helpText}>
                <FormattedMessage id="Verotiedot.vatStatus.help" />
              </p>
              {vatStatus === 'REGISTERED' ? vatNumberField : null}
              {vatStatus === 'EXEMPT_SMALL_BUSINESS' ? optionalVatNumberField : null}
              {vatStatus === 'EXEMPT_SMALL_BUSINESS' && !isFi ? (
                <FieldTextInput
                  type="text"
                  id={formId ? `${formId}.exemptId` : 'exemptId'}
                  name="exemptId"
                  label={intl.formatMessage({ id: 'Verotiedot.exemptId.label' })}
                  placeholder="FI12345678EX"
                  validate={optional()}
                />
              ) : null}
              {vatStatus === 'INVOICING_SERVICE' ? (
                <>
                  <FieldTextInput
                    type="text"
                    id={formId ? `${formId}.invoicingServiceName` : 'invoicingServiceName'}
                    name="invoicingServiceName"
                    label={intl.formatMessage({ id: 'Verotiedot.invoicingService.name' })}
                    placeholder="UKKO"
                    validate={required(informationRequired)}
                  />
                  <FieldTextInput
                    type="text"
                    id={
                      formId ? `${formId}.invoicingServiceVatNumber` : 'invoicingServiceVatNumber'
                    }
                    name="invoicingServiceVatNumber"
                    label={intl.formatMessage({ id: 'Verotiedot.invoicingService.vatNumber' })}
                    placeholder="FI12345678"
                    validate={optional()}
                  />
                </>
              ) : null}
            </div>
          );

          // IBAN — collected only for Finnish freelancers (DAC7 requires the
          // financial-account identifier for payout-receiving sellers).
          const ibanField = showIban ? (
            <div className={css.field}>
              <FieldTextInput
                type="text"
                id={formId ? `${formId}.iban` : 'iban'}
                name="iban"
                label={intl.formatMessage({ id: 'Verotiedot.iban.label' })}
                placeholder="FI21 1234 5600 0007 85"
                format={normalizeIban}
                formatOnBlur
                validate={composeValidators(
                  required(informationRequired),
                  ibanValidation(intl.formatMessage({ id: 'Verotiedot.iban.invalid' }))
                )}
              />
              <p className={css.helpText}>
                <FormattedMessage id="Verotiedot.iban.help" />
              </p>
            </div>
          ) : null;

          // Birth-info block — fallback path for the foreign / no-TIN flow.
          // FI individual flow uses HETU as identifier and never needs this.
          const birthInfoBlock =
            !isFi && noTin ? (
              <div className={css.field}>
                <h4 className={css.sectionTitle}>
                  <FormattedMessage id="Verotiedot.birthInfo.heading" />
                </h4>
                <FieldBirthdayInput
                  id={formId ? `${formId}.dateOfBirth` : 'dateOfBirth'}
                  name="dateOfBirth"
                  label={intl.formatMessage({ id: 'SignupProviderForms.birthdateLabel' })}
                  validate={required(informationRequired)}
                  valueFromForm={initialBirthDate(values?.dateOfBirth)}
                />
                <FieldTextInput
                  type="text"
                  id={formId ? `${formId}.birthCity` : 'birthCity'}
                  name="birthCity"
                  label={intl.formatMessage({ id: 'SignupProviderForms.birthLocationLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupProviderForms.birthLocationLabel' })}
                  validate={required(informationRequired)}
                />
                <FieldSelect
                  id={formId ? `${formId}.birthCountry` : 'birthCountry'}
                  name="birthCountry"
                  label={intl.formatMessage({ id: 'Verotiedot.birthInfo.country' })}
                  validate={required(informationRequired)}
                >
                  {EU_COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>
                      {intl.formatMessage({ id: c.labelKey, defaultMessage: c.code })}
                    </option>
                  ))}
                </FieldSelect>
              </div>
            ) : null;

          const lastSavedNote = values?.submittedAt ? (
            <p className={css.lastSavedNote}>
              <FormattedMessage
                id="Verotiedot.lastSaved"
                values={{ date: intl.formatDate(values.submittedAt) }}
              />
            </p>
          ) : null;

          return (
            <Form className={classes} onSubmit={handleSubmit}>
              {/* Reset identifier fields when country flips, and clear a
                  stale save result once the user edits again. */}
              <FormSpy
                subscription={{ values: true, active: true }}
                onChange={formState => {
                  const v = formState.values;
                  // Only user-driven edits (an active field) clear the save
                  // result — programmatic reinitialization must not wipe the
                  // success message right after saving.
                  if (
                    this.props.onChange &&
                    formState.active &&
                    this._lastSpyValues &&
                    !isEqual(v, this._lastSpyValues)
                  ) {
                    this.props.onChange();
                  }
                  this._lastSpyValues = v;

                  if (!this._lastCountry) this._lastCountry = v.country;
                  if (v.country !== this._lastCountry) {
                    this._lastCountry = v.country;
                    form.mutators.setFields(countryResetPatch(v.country, values));
                    this.setState({ hetuMasked: false, prhResult: null });
                  }

                  // Clear a stale REGISTERED VAT number when the seller
                  // switches to the exempt status (the field stays but
                  // becomes optional) — not on the initial render, and only
                  // for a user-driven edit (formState.active), the same way
                  // the save-result reset above must not fire on a
                  // programmatic reinitialization (e.g. a currentUser
                  // refresh delivering a different vatStatus). A separate
                  // "seen" flag (rather than a falsy check, as vatStatus
                  // legitimately starts undefined) keeps a later first-ever
                  // selection from being mistaken for the initial render.
                  if (!this._vatStatusSeen) {
                    this._vatStatusSeen = true;
                    this._lastVatStatus = v.vatStatus;
                  } else if (v.vatStatus !== this._lastVatStatus) {
                    this._lastVatStatus = v.vatStatus;
                    if (formState.active && v.vatStatus === 'EXEMPT_SMALL_BUSINESS') {
                      form.change('vatNumber', '');
                    }
                  }

                  if (
                    formState.active &&
                    v.country === 'FI' &&
                    v.vatStatus === 'REGISTERED' &&
                    !v.vatNumber &&
                    ytunnusToVat(v.ytunnus)
                  ) {
                    form.change('vatNumber', ytunnusToVat(v.ytunnus));
                  }
                }}
              />
              <div className={css.contactDetailsSection}>
                <p className={css.taxInformationDescription}>
                  <FormattedMessage id="Verotiedot.subtitle" />
                  <br />
                  {taxInformationInfo}
                </p>

                <div className={css.fields}>
                  {countrySelect}
                  {isNonEu ? (
                    <p className={css.helpText}>
                      <FormattedMessage id="Verotiedot.nonEu.explanation" />
                    </p>
                  ) : null}
                  {finCompanyFormRadio}
                  {sellerTypeRadio}
                  {!awaitingClassification ? (
                    <>
                      {!isNonEu ? identifierGroup : null}
                      {!isNonEu ? businessRegBlock : null}
                      {nameGroup}
                      {!isNonEu ? birthInfoBlock : null}
                      <h4 className={css.sectionTitle}>
                        <FormattedMessage id="SignupProviderForms.currentAddressInformationLabel" />
                      </h4>
                      {addressGroup}
                      {vatStatusBlock}
                      {ibanField}
                    </>
                  ) : null}
                </div>

                {taxInformationSuccess ? (
                  <p className={css.successMessage}>
                    <FormattedMessage id="TaxInformationForm.saveSuccessMessage" />
                  </p>
                ) : null}
                {taxInformationSuccess && vatValidationStatus === 'VERIFIED' ? (
                  <p className={css.successMessage}>
                    <FormattedMessage id="TaxInformationForm.vatVerified" />
                  </p>
                ) : null}
                {taxInformationError ? (
                  <p className={css.errorMessage}>
                    <FormattedMessage id="TaxInformationForm.saveErrorMessage" />
                  </p>
                ) : null}
                {lastSavedNote}
                <div className={css.bottomWrapper}>
                  <PrimaryButton
                    type="submit"
                    className={css.submitButton}
                    inProgress={inProgress}
                    disabled={submitDisabled}
                  >
                    <FormattedMessage
                      id={confirmingVatStatus ? 'Verotiedot.confirmVatStatus' : 'Verotiedot.save'}
                    />
                  </PrimaryButton>
                </div>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

TaxInformationFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
  initialMaskedIdentifier: null,
  vatValidationStatus: null,
  vatStatusNeedsConfirmation: false,
};

const { bool, string, object } = PropTypes;

TaxInformationFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  inProgress: bool,
  intl: intlShape.isRequired,
  initialMaskedIdentifier: object,
  vatValidationStatus: string,
  vatStatusNeedsConfirmation: bool,
};

const TaxInformationForm = compose(injectIntl)(TaxInformationFormComponent);

TaxInformationForm.displayName = 'TaxInformationForm';

export default TaxInformationForm;
